import {
    Component,
    computed,
    CUSTOM_ELEMENTS_SCHEMA,
    effect,
    inject,
    input,
    InputSignal,
    output,
    OutputEmitterRef,
    signal,
    Signal,
    WritableSignal
} from '@angular/core';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { IIroNamRequest, IroNamRequestStatus, IUser, Roles, EFileEntity } from '@shs-digi-care-monorepo/interfaces';
import { Icon } from '@shui/angular';
import { select } from '@ngxs/store';
import { MatDialog } from '@angular/material/dialog';
import { catchError, filter, finalize, forkJoin, Observable, of, switchMap, tap } from 'rxjs';
import {
    AssignDialog,
    AssignDialogData,
    AssignDialogResult
} from '../status-change-dialogs/assign-dialog/assign-dialog.component';
import {
    ReAssignDialog,
    ReAssignDialogData,
    ReAssignDialogResult
} from '../status-change-dialogs/reassign-dialog/reassign-dialog.component';
import {
    RequestRequestorFeedbackDialog,
    RequestRequestorFeedbackDialogData,
    RequestRequestorFeedbackDialogResult
} from '../status-change-dialogs/request-requestor-feedback-dialog/request-requestor-feedback-dialog.component';
import {
    ParallelReferralDialog,
    ParallelReferralDialogData,
    ParallelReferralDialogResult,
    CreateReferral,
    ExternalReferralDialog,
    ExternalReferralDialogData,
    ExternalReferralDialogResult
} from '@shs-digi-care-monorepo/frontend-lib';
import {
    ResolveRequestDialog,
    ResolveRequestDialogData,
    ResolveRequestDialogResult
} from '../status-change-dialogs/resolve-request-dialog/resolve-request-dialog.component';
import {
    SetRequestOnHoldDialog,
    SetRequestOnHoldDialogData,
    SetRequestOnHoldDialogResult
} from '../status-change-dialogs/set-request-on-hold-dialog/set-request-on-hold-dialog.component';
import {
    SetRequestActiveDialog,
    SetRequestActiveDialogData,
    SetRequestActiveDialogResult
} from '../status-change-dialogs/set-request-active-dialog/set-request-active-dialog.component';
import { UserState, isTaskOwner, LoggingService, SnackbarService } from '@shs-digi-care-monorepo/frontend-lib';
import { TransitionService, TransitionComment, TransitionReferral } from '../../../shared/services/transition.service';
import { AttachmentService, UploadFileInput } from '../../../shared/services/attachment.service';
import { statusList } from '../status-stepper/status-list.config';
@Component({
    selector: 'app-change-status',
    templateUrl: './change-status.component.html',
    imports: [TranslateModule, Icon],
    schemas: [CUSTOM_ELEMENTS_SCHEMA]
})
export class ChangeStatusComponent {
    public status: InputSignal<IroNamRequestStatus> = input.required<IroNamRequestStatus>();
    public request: InputSignal<IIroNamRequest> = input.required<IIroNamRequest>();

    /** Emits when a transition starts (true) or completes/fails (false). */
    public transitioning: OutputEmitterRef<boolean> = output<boolean>();

    protected states: typeof IroNamRequestStatus = IroNamRequestStatus;
    protected me: Signal<IUser | null> = select(UserState.me);

    private reassignStates: IroNamRequestStatus[] = [IroNamRequestStatus.UNDER_REVIEW];
    private transitionChoices: WritableSignal<IroNamRequestStatus[]> = signal([]);
    private dialog: MatDialog = inject(MatDialog);
    private transitionService: TransitionService = inject(TransitionService);
    private attachmentService: AttachmentService = inject(AttachmentService);
    private logger = inject(LoggingService);
    private snackbar = inject(SnackbarService);
    private translate = inject(TranslateService);

    constructor() {
        effect(() => {
            const request = this.request();
            if (request?.id) {
                this.loadTransitionChoices(request.id);
            }
        });
    }

    public canChangeStatus = computed(() => {
        return this.isUserAdminOrTaskOwner();
    });

    public canPutOnHold = computed(() => {
        return (this.isUserAdmin() || this.isUserAssignee()) && this.canRequestBePutOnHold();
    });

    public canSetActive = computed(() => {
        return this.isUserAdminOrTaskOwner() && this.canRequestBeSetActive();
    });

    public canReassign = computed(() => {
        return this.reassignStates.includes(this.request().status) && !this.request().isOnHold;
    });

    public displayChoicesDropdown = computed((): boolean => {
        const currentStatus = this.request().status;

        return (
            this.transitionChoices().filter((choice) => {
                if (
                    [IroNamRequestStatus.UNDER_REVIEW, IroNamRequestStatus.SUBMITTED].includes(currentStatus) &&
                    choice === IroNamRequestStatus.UNDER_REVIEW
                ) {
                    return this.isUserAdminOrTaskOwner();
                }
                return choice !== IroNamRequestStatus.UNDER_REVIEW;
            }).length > 0
        );
    });

    public canShowUnderReviewButton = computed(() => {
        return this.canShowButtonForward(IroNamRequestStatus.UNDER_REVIEW);
    });

    public canShowPendingCustomerActionButton = computed(() => {
        return this.canShowButtonForward(IroNamRequestStatus.PENDING_CUSTOMER_ACTION);
    });

    public canShowReferredOutButton = computed(() => {
        return this.canShowButtonForward(IroNamRequestStatus.REFERRED_OUT);
    });

    public canShowClosedButton = computed(() => {
        return this.canShowButtonForward(IroNamRequestStatus.CLOSED);
    });

    protected handleAssignTo(): void {
        this.dialog
            .open<AssignDialog, AssignDialogData, AssignDialogResult>(AssignDialog, {
                width: '500px',
                panelClass: 'status-change-dialog-panel',
                hasBackdrop: false
            })
            .afterClosed()
            .pipe(
                filter((result) => !!result && !!result.gid),
                switchMap((result) => {
                    this.logger.info('Assigning IRO request', { requestId: this.request().id, gid: result?.gid });
                    this.transitioning.emit(true);
                    return this.transitionService.assign(this.request().id, result?.gid ?? '').pipe(
                        catchError((error) => {
                            this.logger.error('Failed to assign IRO request', {
                                requestId: this.request().id,
                                error: error?.message ?? error
                            });
                            this.snackbar.error('iro.messages.error-transitioning');
                            return of(void 0);
                        }),
                        finalize(() => this.transitioning.emit(false))
                    );
                })
            )
            .subscribe();
    }

    protected handlePutRequestOnHoldDialog(): void {
        this.dialog
            .open<SetRequestOnHoldDialog, SetRequestOnHoldDialogData, SetRequestOnHoldDialogResult>(
                SetRequestOnHoldDialog,
                {
                    width: '500px',
                    panelClass: 'status-change-dialog-panel',
                    hasBackdrop: false
                }
            )
            .afterClosed()
            .pipe(
                filter((result) => !!result),
                switchMap((result) => {
                    this.logger.info('Putting IRO request on hold', { requestId: this.request().id });
                    this.transitioning.emit(true);
                    return this.transitionService.setOnHold(this.request().id, result.comment).pipe(
                        tap(() => this.snackbar.success('iro.messages.put-on-hold-success')),
                        catchError((error) => {
                            this.logger.error('Failed to put IRO request on hold', {
                                requestId: this.request().id,
                                error: error?.message ?? error
                            });
                            this.snackbar.error('iro.messages.error-transitioning');
                            return of(void 0);
                        }),
                        finalize(() => this.transitioning.emit(false))
                    );
                })
            )
            .subscribe();
    }

    protected handleOpenSetRequestActiveDialog(): void {
        this.dialog
            .open<SetRequestActiveDialog, SetRequestActiveDialogData, SetRequestActiveDialogResult>(
                SetRequestActiveDialog,
                {
                    width: '500px',
                    panelClass: 'status-change-dialog-panel',
                    hasBackdrop: false
                }
            )
            .afterClosed()
            .pipe(
                filter((result) => !!result),
                switchMap(() => {
                    this.logger.info('Setting IRO request active', { requestId: this.request().id });
                    this.transitioning.emit(true);
                    return this.transitionService.setActive(this.request().id).pipe(
                        tap(() => this.snackbar.success('iro.messages.set-active-success')),
                        catchError((error) => {
                            this.logger.error('Failed to set IRO request active', {
                                requestId: this.request().id,
                                error: error?.message ?? error
                            });
                            this.snackbar.error('iro.messages.error-transitioning');
                            return of(void 0);
                        }),
                        finalize(() => this.transitioning.emit(false))
                    );
                })
            )
            .subscribe();
    }

    protected handleAsignRequest(): void {
        const myGid = this.me()?.gid;
        if (!myGid) return;
        this.logger.info('Self-assigning IRO request', { requestId: this.request().id, gid: myGid });
        this.executeTransition(this.transitionService.assign(this.request().id, myGid));
    }

    protected handleOpenReassignRequestDialog(): void {
        this.dialog
            .open<ReAssignDialog, ReAssignDialogData, ReAssignDialogResult>(ReAssignDialog, {
                width: '500px',
                panelClass: 'status-change-dialog-panel',
                hasBackdrop: false
            })
            .afterClosed()
            .pipe(
                filter((result) => !!result),
                switchMap((result) => {
                    this.logger.info('Reassigning IRO request', {
                        requestId: this.request().id,
                        gid: result.gid,
                        teamId: result.teamId
                    });
                    this.transitioning.emit(true);
                    return this.transitionService
                        .reassign(this.request().id, {
                            assignToGid: result.gid ?? undefined,
                            teamId: result.teamId ?? undefined
                        })
                        .pipe(
                            catchError((error) => {
                                this.logger.error('Failed to reassign IRO request', {
                                    requestId: this.request().id,
                                    error: error?.message ?? error
                                });
                                this.snackbar.error('iro.messages.error-transitioning');
                                return of(void 0);
                            }),
                            finalize(() => this.transitioning.emit(false))
                        );
                })
            )
            .subscribe();
    }

    protected handleOpenDemandCustomerFeedbackDialog(): void {
        this.dialog
            .open<
                RequestRequestorFeedbackDialog,
                RequestRequestorFeedbackDialogData,
                RequestRequestorFeedbackDialogResult
            >(RequestRequestorFeedbackDialog, {
                width: '500px',
                panelClass: 'status-change-dialog-panel',
                hasBackdrop: false
            })
            .afterClosed()
            .pipe(
                filter((result) => !!result && !!result.comment),
                switchMap((result) => {
                    this.logger.info('Requesting customer feedback for IRO request', {
                        requestId: this.request().id
                    });
                    this.transitioning.emit(true);
                    const files = result?.attachments?.map((item) => item.file) ?? [];
                    return this.uploadAttachments(files).pipe(
                        switchMap((fileNames) => {
                            const comment: TransitionComment = {
                                body: result?.comment ?? '',
                                ...(fileNames.length && { files: fileNames })
                            };
                            return this.transitionService.requestFeedback(this.request().id, comment).pipe(
                                tap(() => this.snackbar.success('iro.messages.customer-feedback-requested')),
                                catchError((error) => {
                                    this.logger.error('Failed to request customer feedback for IRO request', {
                                        requestId: this.request().id,
                                        error: error?.message ?? error
                                    });
                                    this.snackbar.error('iro.messages.error-transitioning');
                                    return of(void 0);
                                }),
                                finalize(() => this.transitioning.emit(false))
                            );
                        })
                    );
                })
            )
            .subscribe();
    }

    protected handleOpenExternalReferOutDialog(): void {
        this.dialog
            .open<ExternalReferralDialog, ExternalReferralDialogData, ExternalReferralDialogResult>(
                ExternalReferralDialog,
                {
                    panelClass: 'sheet-panel-right',
                    hasBackdrop: true,
                    data: { canMarkCommentAsInternal: true }
                }
            )
            .afterClosed()
            .pipe(
                filter((result) => !!result),
                switchMap((result) => {
                    this.logger.info('Referring IRO request out externally', {
                        requestId: this.request().id,
                        email: result.email
                    });
                    this.transitioning.emit(true);
                    const referrals: TransitionReferral[] = [
                        {
                            name: result.name,
                            description: result.description ?? undefined,
                            referredOutEmail: result.email,
                            isInternalReferredOut: false,
                            markCommentInternal: result.markAsInternal
                        }
                    ];
                    return this.transitionService.referOut(this.request().id, referrals).pipe(
                        catchError((error) => {
                            this.logger.error('Failed to refer IRO request out externally', {
                                requestId: this.request().id,
                                error: error?.message ?? error
                            });
                            this.snackbar.error('iro.messages.error-transitioning');
                            return of(void 0);
                        }),
                        finalize(() => this.transitioning.emit(false))
                    );
                })
            )
            .subscribe();
    }

    protected handleOpenInternalReferOutDialog(): void {
        this.dialog
            .open<ParallelReferralDialog, ParallelReferralDialogData, ParallelReferralDialogResult>(
                ParallelReferralDialog,
                {
                    panelClass: 'sheet-panel-right',
                    hasBackdrop: true,
                    data: {
                        supportsParallelReferral: true,
                        canMarkCommentAsInternal: true
                    }
                }
            )
            .afterClosed()
            .pipe(
                filter((result) => !!result),
                switchMap((result) => {
                    this.logger.info('Referring IRO request out', {
                        requestId: this.request().id,
                        referralCount: result.entries.length
                    });
                    this.transitioning.emit(true);
                    const referrals: TransitionReferral[] = result.entries.map((entry) => ({
                        name: entry.name,
                        description: entry.description ?? undefined,
                        referredOutTeam: entry.assignee?.type === 'team' ? entry.assignee.id : undefined,
                        referredOutEmail: entry.assignee?.type === 'user' ? entry.assignee.email : undefined,
                        isInternalReferredOut: true,
                        markCommentInternal: entry.markAsInternal
                    }));
                    return this.transitionService.referOut(this.request().id, referrals).pipe(
                        catchError((error) => {
                            this.logger.error('Failed to refer IRO request out', {
                                requestId: this.request().id,
                                error: error?.message ?? error
                            });
                            this.snackbar.error('iro.messages.error-transitioning');
                            return of(void 0);
                        }),
                        finalize(() => this.transitioning.emit(false))
                    );
                })
            )
            .subscribe();
    }

    protected handleOpenCloseRequestDialog(): void {
        this.dialog
            .open<ResolveRequestDialog, ResolveRequestDialogData, ResolveRequestDialogResult>(ResolveRequestDialog, {
                width: '500px',
                panelClass: 'status-change-dialog-panel',
                hasBackdrop: false
            })
            .afterClosed()
            .pipe(
                filter((result) => !!result),
                switchMap((result) => {
                    this.logger.info('Resolving IRO request', { requestId: this.request().id });
                    this.transitioning.emit(true);
                    const files = result.attachments?.map((item) => item.file) ?? [];
                    return this.uploadAttachments(files).pipe(
                        switchMap((fileNames) => {
                            const comment: TransitionComment | undefined = result.comment
                                ? { body: result.comment, ...(fileNames.length && { files: fileNames }) }
                                : undefined;
                            return this.transitionService.resolve(this.request().id, comment).pipe(
                                tap(() => this.snackbar.success('iro.messages.resolved-success')),
                                catchError((error) => {
                                    this.logger.error('Failed to resolve IRO request', {
                                        requestId: this.request().id,
                                        error: error?.message ?? error
                                    });
                                    this.snackbar.error('iro.messages.error-transitioning');
                                    return of(void 0);
                                }),
                                finalize(() => this.transitioning.emit(false))
                            );
                        })
                    );
                })
            )
            .subscribe();
    }

    /**
     * Executes a transition observable, emitting transitioning state.
     */
    private executeTransition(transition$: Observable<void>): void {
        this.transitioning.emit(true);
        transition$
            .pipe(
                catchError((error) => {
                    this.logger.error('IRO status transition failed', {
                        requestId: this.request().id,
                        error: error?.message ?? error
                    });
                    this.snackbar.error('iro.messages.error-transitioning');
                    return of(void 0);
                }),
                finalize(() => this.transitioning.emit(false))
            )
            .subscribe();
    }

    /**
     * Uploads files to S3 and returns their file names.
     */
    private uploadAttachments(files: File[]): Observable<string[]> {
        if (!files.length) return of([]);
        return forkJoin(
            files.map((file) => {
                const input: UploadFileInput = {
                    fileEntity: EFileEntity.Request,
                    requestId: this.request().id,
                    file,
                    fileName: file.name
                };
                return this.attachmentService.uploadFile(input);
            })
        ).pipe(switchMap(() => of(files.map((f) => f.name))));
    }

    /**
     * Loads allowed transition choices from the API.
     */
    private loadTransitionChoices(requestId: string): void {
        this.transitionService
            .getTransitionOptions(requestId)
            .pipe(tap((response) => this.transitionChoices.set(response.options)))
            .subscribe();
    }

    protected canShowButtonForward(status: IroNamRequestStatus): boolean {
        const canShow = this.transitionChoices().includes(status);
        const statusIndex = statusList.findIndex((statusItem) => statusItem.values.includes(this.request().status));

        const indexOfButtonStatus = statusList.findIndex((statusItem) => statusItem.values.includes(status));
        return canShow && statusIndex < indexOfButtonStatus && !this.request().isOnHold;
    }

    private canRequestBePutOnHold = computed((): boolean => {
        return this.request().status !== IroNamRequestStatus.SUBMITTED && !this.request().isOnHold;
    });

    private canRequestBeSetActive = computed((): boolean => {
        return this.request().status !== IroNamRequestStatus.SUBMITTED && this.request().isOnHold;
    });

    private isUserAdminOrTaskOwner = computed((): boolean => {
        return !!this.me() && isTaskOwner(this.me() ?? null);
    });

    private isUserAdmin = computed((): boolean => {
        const me = this.me();
        return !!me && !!me.roles?.includes(Roles.ADMIN);
    });

    private isUserAssignee = computed((): boolean => {
        const me = this.me();
        const request = this.request() as IIroNamRequest;
        return !!me && !!me.roles?.includes(Roles.TASK_OWNER) && request.assignee === me.gid;
    });
}
